/**
 * AudioContext acquisition — the ONE file in `src/audio` allowed to touch the host globals.
 * `tools/lint-determinism.mjs` exempts exactly this path (DOM_EXEMPT_PATTERNS); every other
 * module in this subsystem receives an AudioContext-like object as a parameter, which is what
 * lets `npm run test` drive the whole graph with a stub in plain Node.
 *
 * Browsers refuse to start an AudioContext without a user gesture. The policy here:
 *   - `isAvailable()` reports whether the host has Web Audio at all (false in Node).
 *   - `ensureContext()` creates the context immediately and — if the browser holds it suspended —
 *     arms one-shot gesture listeners. It does not call `resume()` before a trusted gesture,
 *     because Chromium reports that expected refusal as a console warning. The returned promise
 *     resolves with a RUNNING context, or with `null` when Web Audio does not exist.
 *   - Nothing in the engine ever awaits that promise on the critical path, so a headless
 *     deterministic run (where it never resolves at all) cannot stall.
 */

/** Gestures the browser accepts as "user activation". */
const GESTURE_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'touchend', 'keydown'];

/** @type {AudioContext|null} */
let _ac = null;
/** 'idle' | 'pending' | 'running' | 'unavailable' | 'closed' */
let _state = 'idle';
/** @type {Promise<AudioContext|null>|null} */
let _promise = null;
/** @type {((ac: AudioContext|null) => void)|null} */
let _resolve = null;
/** @type {(() => void)|null} */
let _detach = null;
/** Test seam: lets a unit test inject a stub constructor without a DOM. */
let _factory = null;

/** The global object, or null in an exotic host. Never dereferenced blindly. */
function scope() {
  return typeof globalThis === 'undefined' ? null : globalThis;
}

/** The object that dispatches user-gesture events (the window in a browser). */
function eventTarget() {
  const g = scope();
  if (!g) return null;
  const w = g.window;
  if (w && typeof w.addEventListener === 'function') return w;
  if (typeof g.addEventListener === 'function') return g;
  return null;
}

function contextCtor() {
  if (_factory) return _factory;
  const g = scope();
  if (!g) return null;
  if (typeof g.AudioContext === 'function') return g.AudioContext;
  if (typeof g.webkitAudioContext === 'function') return g.webkitAudioContext;
  return null;
}

/**
 * Overrides the constructor used by `ensureContext`. Unit tests pass a stub that records the
 * graph shape; passing `null` restores the host lookup. Never called by engine code.
 * @param {Function|null} ctor
 */
export function setContextFactory(ctor) {
  _factory = typeof ctor === 'function' ? ctor : null;
}

/** True when this host can produce an AudioContext at all. False in Node. */
export function isAvailable() {
  return contextCtor() !== null;
}

/** The live context, or null before the first gesture. Never throws. */
export function getContext() {
  return _ac;
}

/** 'idle' | 'pending' | 'running' | 'unavailable' | 'closed'. */
export function audioState() {
  return _state;
}

/** True once the context exists and is actually producing samples. */
export function isRunning() {
  return _ac !== null && _ac.state === 'running';
}

/**
 * The audio hardware clock in seconds, or 0 when there is no context. Callers map
 * `ctx.time.simSeconds` onto this via a single offset (see AudioSystem._syncTimeBase) so that
 * scheduling stays on the audio clock while all *content* stays on the simulation clock.
 */
export function audioNow() {
  return _ac === null ? 0 : _ac.currentTime;
}

/**
 * Creates (once) and unlocks the AudioContext.
 * @param {{ sampleRate?: number, latencyHint?: string|number }} [options]
 * @returns {Promise<AudioContext|null>} resolves with a running context, or null when the host
 *   has no Web Audio. Safe to call repeatedly — the same promise is returned.
 */
export function ensureContext(options = {}) {
  if (_promise) return _promise;

  const Ctor = contextCtor();
  if (!Ctor) {
    _state = 'unavailable';
    _promise = Promise.resolve(null);
    return _promise;
  }

  _promise = new Promise((resolve) => {
    _resolve = resolve;
  });
  _state = 'pending';

  try {
    const args = { latencyHint: options.latencyHint || 'interactive' };
    if (options.sampleRate) args.sampleRate = options.sampleRate;
    _ac = new Ctor(args);
  } catch {
    // Some hosts reject the options bag; retry bare before giving up.
    try {
      _ac = new Ctor();
    } catch {
      _ac = null;
    }
  }

  if (!_ac) {
    _state = 'unavailable';
    _settle(null);
    return _promise;
  }

  if (_ac.state === 'running') {
    _state = 'running';
    _settle(_ac);
    return _promise;
  }

  _armGesture();
  return _promise;
}

/**
 * Arms one-shot listeners on every gesture event. The first gesture resumes the context and
 * detaches all of them, so this costs nothing after unlock.
 */
function _armGesture() {
  const target = eventTarget();
  if (!target) {
    // No event source (worker, exotic host): the context stays suspended and every cue is
    // dropped, which is the correct silent degradation.
    return;
  }
  const onGesture = () => {
    const resume = _tryResume();
    // `resume()` is async in some browsers; wait for its promise before reading state.
    Promise.resolve(resume).then(() => {
      if (_ac && _ac.state === 'running') {
        _state = 'running';
        if (_detach) _detach();
        _settle(_ac);
      }
    });
  };
  const opts = { passive: true };
  for (const type of GESTURE_EVENTS) target.addEventListener(type, onGesture, opts);
  _detach = () => {
    for (const type of GESTURE_EVENTS) target.removeEventListener(type, onGesture, opts);
    _detach = null;
  };
}

function _tryResume() {
  if (!_ac || typeof _ac.resume !== 'function') return null;
  try {
    const p = _ac.resume();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    return p || null;
  } catch {
    // Autoplay policy refusal before a gesture: expected, not an error.
    return null;
  }
}

function _settle(value) {
  const fn = _resolve;
  _resolve = null;
  if (fn) fn(value);
}

/**
 * Tears the context down. Used by `AudioSystem.dispose()` and by tools between captures so a
 * mode-switch cycle cannot leak audio hardware handles.
 * @returns {Promise<void>}
 */
export function closeContext() {
  if (_detach) _detach();
  const ac = _ac;
  _ac = null;
  _promise = null;
  _resolve = null;
  _state = 'closed';
  if (!ac || typeof ac.close !== 'function') return Promise.resolve();
  try {
    const p = ac.close();
    return p && typeof p.then === 'function' ? p.then(() => {}, () => {}) : Promise.resolve();
  } catch {
    return Promise.resolve();
  }
}
